import { supabase } from "@/integrations/supabase/client";

type VoiceConnectionType = "webrtc" | "websocket";

type VoiceConversationController = {
  startSession: (options: {
    conversationToken?: string;
    signedUrl?: string;
    connectionType?: VoiceConnectionType;
  }) => void | Promise<unknown>;
};

type ElevenLabsSessionPayload = {
  token?: string;
  signedUrl?: string;
};

const RECOVERABLE_CONNECTION_ERROR_PATTERN = /pc connection|peer connection|webrtc|ice|rtc/i;
const VOICE_DEBUG_PREFIX = "[VoiceSession]";

const getErrorLogDetails = (error: unknown) => ({
  name: error instanceof Error ? error.name : undefined,
  message: error instanceof Error ? error.message : String(error ?? ""),
});

export const logVoiceSessionDebug = (event: string, details?: Record<string, unknown>) => {
  if (typeof console === "undefined") return;
  console.info(VOICE_DEBUG_PREFIX, event, details ?? {});
};

const isIosLikeBrowser = () => {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;
  return /iP(hone|ad|od)/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
};

const getSessionPayload = async (agentId: string): Promise<ElevenLabsSessionPayload> => {
  logVoiceSessionDebug("session_payload_requested", { agentId });

  const { data, error } = await supabase.functions.invoke("elevenlabs-token", {
    body: { agentId },
  });

  if (error) {
    logVoiceSessionDebug("session_payload_failed", {
      agentId,
      ...getErrorLogDetails(error),
    });
    throw new Error(error.message);
  }

  const token = typeof data?.token === "string" ? data.token : undefined;
  const signedUrl = typeof data?.signedUrl === "string" ? data.signedUrl : undefined;

  logVoiceSessionDebug("session_payload_received", {
    agentId,
    hasToken: Boolean(token),
    hasSignedUrl: Boolean(signedUrl),
  });

  if (!token && !signedUrl) {
    throw new Error("Token de session manquant depuis le serveur");
  }

  return { token, signedUrl };
};

const startWebrtcSession = async (
  conversation: VoiceConversationController,
  conversationToken: string,
) => {
  await conversation.startSession({
    conversationToken,
    connectionType: "webrtc",
  });
};

const startWebsocketSession = async (
  conversation: VoiceConversationController,
  signedUrl: string,
) => {
  await conversation.startSession({
    signedUrl,
    connectionType: "websocket",
  });
};

export const isRecoverableVoiceConnectionError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return RECOVERABLE_CONNECTION_ERROR_PATTERN.test(message);
};

export const startVoiceAgentSession = async ({
  conversation,
  agentId,
}: {
  conversation: VoiceConversationController;
  agentId: string;
}): Promise<VoiceConnectionType> => {
  const iosLikeBrowser = isIosLikeBrowser();
  logVoiceSessionDebug("session_start_requested", {
    agentId,
    iosLikeBrowser,
  });

  const session = await getSessionPayload(agentId);

  if (session.signedUrl) {
    logVoiceSessionDebug("transport_selected", {
      agentId,
      transport: "websocket",
      reason: iosLikeBrowser ? "ios_like_browser" : "compatibility_first",
      hasWebrtcToken: Boolean(session.token),
    });
    await startWebsocketSession(conversation, session.signedUrl);
    logVoiceSessionDebug("transport_connected", {
      agentId,
      transport: "websocket",
    });
    return "websocket";
  }

  if (!session.token) {
    logVoiceSessionDebug("transport_unavailable", {
      agentId,
      hasToken: false,
      hasSignedUrl: false,
    });
    throw new Error("Token de conversation absent.");
  }

  try {
    logVoiceSessionDebug("transport_selected", {
      agentId,
      transport: "webrtc",
      reason: "signed_url_unavailable",
    });
    await startWebrtcSession(conversation, session.token);
    logVoiceSessionDebug("transport_connected", {
      agentId,
      transport: "webrtc",
    });
    return "webrtc";
  } catch (error) {
    logVoiceSessionDebug("transport_failed", {
      agentId,
      transport: "webrtc",
      recoverable: isRecoverableVoiceConnectionError(error),
      ...getErrorLogDetails(error),
    });
    throw error;
  }
};

export const formatVoiceSessionError = (error: unknown) => {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "Unknown error";

  if (/Permission|denied|NotAllowedError/i.test(message)) {
    return "Autorisez le microphone dans votre navigateur.";
  }

  if (isRecoverableVoiceConnectionError(error)) {
    return "La connexion audio directe a échoué. Réessayez : le site basculera automatiquement sur un mode compatible mobile.";
  }

  return `ElevenLabs: ${message}`;
};
